<template>
  <div class="homepage">
    <div class="flex mb-0 surface-50 px-2 py-2 mb-2 border-round-sm">
      <div class="flex-0 flex align-items-center justify-content-center">
        <Button
          icon="pi pi-chevron-left"
          aria-label="Previous day"
          size="small"
          class="surface-300 hover:surface-500 mr-2"
          @click="changeDay(-1)"
        />
        <div class="px-0 py-2">
          <p class="p-0 m-0 font-bold">{{ formattedSelectedDate }}</p>
        </div>
        <Button
          icon="pi pi-chevron-right"
          aria-label="Next day"
          size="small"
          class="surface-300 hover:surface-500 ml-3"
          :disabled="isToday"
          @click="changeDay(1)"
        />
      </div>

      <div class="flex-1 flex align-items-center justify-content-end">
        <div v-if="isToday" class="flex flex-wrap justify-content-start gap-1 mr-3">
          <Badge class="surface-100 text-300 w-3rem py-0 mt-0">{{ formattedCountdown }}</Badge>
        </div>
        <div class="flex flex-wrap justify-content-start gap-1 mr-3 text-sm">
          <Badge class="surface-300 px-1 py-0 mt-0">{{ managers.length }}</Badge>
          <strong class="uppercase text-400 pt-1 font-normal">MNGR</strong>
        </div>
        <div class="flex flex-wrap justify-content-start gap-1 mr-3 text-sm">
          <Badge class="surface-300 px-1 py-0 mt-0">{{ itStaff.length }}</Badge>
          <strong class="uppercase text-400 pt-1 font-normal">IT</strong>
        </div>
        <div class="flex flex-wrap justify-content-start gap-1 mr-3 text-sm">
          <Badge class="surface-300 px-1 py-0 mt-0">{{ coveringStaff.length }}</Badge>
          <strong class="uppercase text-400 pt-1 font-normal">COVER</strong>
        </div>
        <div class="flex flex-wrap justify-content-start gap-1 mr-3 text-sm">
          <Badge class="surface-300 px-1 py-0 mt-0">{{ maintenanceStaff.length }}</Badge>
          <strong class="uppercase text-400 pt-1 font-normal">Maint</strong>
        </div>
        <div class="flex flex-wrap justify-content-start gap-1 mr-3 text-sm">
          <Badge class="surface-300 px-1 py-0 mt-0">{{ houseKeeping.length }}</Badge>
          <strong class="uppercase text-400 pt-1 font-normal">Keep</strong>
        </div>
        <div class="flex flex-wrap justify-content-start gap-1 mr-3 text-sm">
          <Badge class="surface-300 px-1 py-0 mt-0">{{ securityStaff.length }}</Badge>
          <strong class="uppercase text-400 pt-1 font-normal">Guard</strong>
        </div>
        <div class="flex flex-wrap justify-content-start gap-1 mr-3">
          <Badge class="bg-green-400 px-3 py-0 mt-0">{{ openAttendance.length }}</Badge>
          <strong class="uppercase text-600 ml-1 pt-1 font-normal">Online</strong>
        </div>
        <div class="flex flex-wrap justify-content-start gap-1 mr-3">
          <Badge class="surface-300 px-3 py-0 mt-0">{{ filteredAttendance.length }}</Badge>
          <strong class="uppercase text-600 ml-1 pt-1 font-normal">Worked</strong>
        </div>
        <InputText v-model="filterText" placeholder="Quick Search" class="mr-3" />
        <Button
          icon="pi pi-refresh"
          aria-label="Refresh dashboard"
          :loading="isLoading"
          class="surface-300 hover:surface-500 mr-2"
          @click="handleUpdateSuccess"
        />
      </div>
    </div>

    <Message v-if="dashboardError" severity="error" :closable="false" class="mb-3">
      {{ dashboardError }}
    </Message>

    <div v-if="dashboardDataLoading">
      <div class="imab-logo-animated">
        <div><span class="I">I</span><span class="M">M</span><span class="A">A</span><span class="B">B</span></div>
      </div>
    </div>

    <template v-else>
      <section v-for="day in dashboardDays" :key="day.date" class="dashboard-day mb-4">
        <header v-if="isToday" class="dashboard-day-header flex align-items-center gap-2 mb-2 px-1">
          <span class="font-bold text-600">{{ day.isToday ? 'Today' : 'Yesterday' }}</span>
          <span class="text-sm text-400">{{ day.formattedDate }}</span>
        </header>

        <Message v-if="day.error" severity="warn" :closable="false" class="mb-2">
          {{ day.error }}
        </Message>

        <div class="dashboard-sections flex flex-wrap justify-content-start gap-0">
          <section-status
            :title="day.isToday ? 'UPCOMING' : 'ONTIME'"
            :records="day.isToday ? day.upcomingRecords : day.ontimeRecords"
            :sectionClass="day.isToday ? 'upcoming w-2' : 'ontime w-2'"
            :showCounter="true"
          />
          <section-status
            title="LEAVES"
            :records="day.leaveRecords"
            :sectionClass="'leaves w-2'"
            :showCounter="false"
          />
          <section-status
            title="LATE"
            :records="day.lateRecords"
            :sectionClass="'late w-2'"
            :showCounter="true"
          />
          <section-status
            title="EARLY"
            :records="day.earlyRecords"
            :sectionClass="'early w-2'"
            :showCounter="true"
          />
          <section-status
            title="OVERTIME"
            :records="day.overtimeRecords"
            :sectionClass="'overtime w-2'"
            :showCounter="true"
          />
          <div class="w-2">
            <section-status
              v-if="day.isToday"
              title="NOTLOGOUT"
              :records="day.notLogoutRecords"
              :sectionClass="'notlogout'"
              :showCounter="true"
            />
            <section-status
              title="COVERS"
              :records="day.coverRecords"
              :sectionClass="'covers'"
              :showCounter="true"
              :style="{ marginTop: day.isToday ? '20px' : '0' }"
            />
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Badge from 'primevue/badge';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Message from 'primevue/message';
import SectionStatus from '@/components/dashboard/SectionStatus.vue';
import { useFetchDashboardDay } from '@/composables/api/dashboard/useFetchDashboardDay';
import { useLondonUnixTime } from '@/composables/time/useLondonTime';
import {
  addDashboardDays,
  buildDashboardRoute,
  getLondonTodayDate,
  parseDashboardRouteDate
} from '@/composables/useDashboardRoute';

const REFRESH_INTERVAL = 60 * 1000;

const route = useRoute();
const router = useRouter();
const { getLondonUnixTime } = useLondonUnixTime();
const { fetchDashboardDay } = useFetchDashboardDay();

const selectedDayData = ref(null);
const yesterdayData = ref(null);
const yesterdayError = ref(null);
const filterText = ref('');
const isLoading = ref(false);
const hasLoaded = ref(false);
const dashboardError = ref(null);
const currentTimeUnix = ref(getLondonUnixTime());
const timeUntilNextRefresh = ref(REFRESH_INTERVAL);

let refreshTimer;
let countdownTimer;
let requestController;
let requestSequence = 0;

const parsedDashboardRoute = computed(() => parseDashboardRouteDate(route.params));
const selectedDate = computed(() => parsedDashboardRoute.value.date || '');
const isToday = computed(() => parsedDashboardRoute.value.valid && parsedDashboardRoute.value.isToday);
const dashboardDataLoading = computed(() => isLoading.value && !hasLoaded.value);

const selectedDateObject = computed(() => new Date(`${selectedDate.value || getLondonTodayDate()}T00:00:00Z`));
const formattedSelectedDate = computed(() => new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC'
}).format(selectedDateObject.value));

const employeeMatchesFilter = record => {
  const search = filterText.value.trim().toLowerCase();
  return !search || record?.employee_name?.toLowerCase().includes(search);
};

const formatDashboardDate = date => new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC'
}).format(new Date(`${date}T00:00:00Z`));

const normalizeDashboardData = (response, date) => ({
  date,
  rosters: response?.rosters || [],
  attendance: response?.attendance || [],
  notLogout: response?.not_logout || []
});

const buildDashboardDay = (data, dayIsToday, error = null) => {
  const filteredRosters = (data?.rosters || []).filter(employeeMatchesFilter);
  const filteredAttendance = (data?.attendance || []).filter(employeeMatchesFilter);
  const openAttendance = filteredAttendance.filter(record => record.attendance_out == null);
  const threeHoursLater = currentTimeUnix.value + (3 * 60 * 60 * 1000);

  return {
    date: data?.date,
    formattedDate: data?.date ? formatDashboardDate(data.date) : '',
    isToday: dayIsToday,
    error,
    filteredAttendance,
    openAttendance,
    managers: openAttendance.filter(record => record.employee_department === 'Manager'),
    itStaff: openAttendance.filter(record => record.employee_department === 'Tech'),
    maintenanceStaff: openAttendance.filter(record => record.employee_department === 'Maintenance'),
    houseKeeping: openAttendance.filter(record => record.employee_department === 'House Keeping'),
    securityStaff: openAttendance.filter(record => record.employee_department === 'Security'),
    coveringStaff: openAttendance.filter(record => record.event_type === 'COVER'),
    upcomingRecords: filteredRosters.filter(record =>
      ['SHIFT', 'COVER'].includes(record.event_type)
      && record.event_start >= currentTimeUnix.value
      && record.event_start <= threeHoursLater
    ),
    leaveRecords: filteredRosters
      .filter(record => {
        if (record.event_type === 'LEAVE') return true;
        return ['SHIFT', 'COVER'].includes(record.event_type)
          && (!dayIsToday || record.event_start < currentTimeUnix.value);
      })
      .sort((a, b) => {
        if (a.event_type === 'LEAVE' && b.event_type !== 'LEAVE') return -1;
        if (b.event_type === 'LEAVE' && a.event_type !== 'LEAVE') return 1;
        return b.event_start - a.event_start;
      }),
    ontimeRecords: filteredAttendance.filter(record =>
      record.event_type !== 'COVER'
      && record.attendance_status_in === 'ONTIME'
      && ['ONTIME', 'OVERTIME'].includes(record.attendance_status_out)
    ),
    lateRecords: filteredAttendance
      .filter(record => record.attendance_status_in === 'LATE')
      .sort((a, b) => b.attendance_in - a.attendance_in),
    earlyRecords: filteredAttendance
      .filter(record => record.attendance_status_in === 'EARLY' || record.attendance_status_out === 'EARLY')
      .sort((a, b) => (b.attendance_out || 0) - (a.attendance_out || 0)),
    overtimeRecords: filteredAttendance
      .filter(record => record.attendance_status_out === 'OVERTIME')
      .sort((a, b) => (b.attendance_out || 0) - (a.attendance_out || 0)),
    coverRecords: filteredAttendance.filter(record => record.event_type === 'COVER'),
    notLogoutRecords: dayIsToday
      ? (data?.notLogout || []).filter(employeeMatchesFilter).sort((a, b) => a.event_end - b.event_end)
      : []
  };
};

const selectedDashboardDay = computed(() => buildDashboardDay(selectedDayData.value, isToday.value));
const dashboardDays = computed(() => {
  const days = [selectedDashboardDay.value];
  if (isToday.value && (yesterdayData.value || yesterdayError.value)) {
    const fallbackDate = addDashboardDays(selectedDate.value, -1);
    days.push(buildDashboardDay(
      yesterdayData.value || normalizeDashboardData(null, fallbackDate),
      false,
      yesterdayError.value
    ));
  }
  return days;
});

const filteredAttendance = computed(() => selectedDashboardDay.value.filteredAttendance);
const openAttendance = computed(() => selectedDashboardDay.value.openAttendance);

const managers = computed(() => selectedDashboardDay.value.managers);
const itStaff = computed(() => selectedDashboardDay.value.itStaff);
const maintenanceStaff = computed(() => selectedDashboardDay.value.maintenanceStaff);
const houseKeeping = computed(() => selectedDashboardDay.value.houseKeeping);
const securityStaff = computed(() => selectedDashboardDay.value.securityStaff);
const coveringStaff = computed(() => selectedDashboardDay.value.coveringStaff);

const formattedCountdown = computed(() => {
  const minutes = Math.floor(timeUntilNextRefresh.value / 60000);
  const seconds = Math.floor((timeUntilNextRefresh.value % 60000) / 1000);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
});

const clearTimers = () => {
  clearInterval(refreshTimer);
  clearInterval(countdownTimer);
};

const startAutoRefresh = () => {
  clearTimers();
  timeUntilNextRefresh.value = REFRESH_INTERVAL;
  if (!isToday.value) return;

  refreshTimer = setInterval(async () => {
    await refreshDashboard({ includeYesterday: false });
    timeUntilNextRefresh.value = REFRESH_INTERVAL;
  }, REFRESH_INTERVAL);
  countdownTimer = setInterval(() => {
    timeUntilNextRefresh.value = Math.max(0, timeUntilNextRefresh.value - 1000);
  }, 1000);
};

const refreshDashboard = async ({ includeYesterday = false } = {}) => {
  if (!parsedDashboardRoute.value.valid) return;

  requestController?.abort();
  requestController = new AbortController();
  const requestId = ++requestSequence;
  isLoading.value = true;
  dashboardError.value = null;
  currentTimeUnix.value = getLondonUnixTime();

  try {
    const loadYesterday = isToday.value && includeYesterday;
    const previousDate = addDashboardDays(selectedDate.value, -1);
    const requests = [fetchDashboardDay(selectedDate.value, requestController.signal)];
    if (loadYesterday) {
      requests.push(fetchDashboardDay(previousDate, requestController.signal));
    }

    const [selectedResult, previousResult] = await Promise.allSettled(requests);
    if (requestId !== requestSequence) return;

    if (selectedResult.status === 'rejected') {
      throw selectedResult.reason;
    }

    selectedDayData.value = normalizeDashboardData(selectedResult.value, selectedDate.value);
    hasLoaded.value = true;

    if (loadYesterday) {
      if (previousResult.status === 'fulfilled') {
        yesterdayData.value = normalizeDashboardData(previousResult.value, previousDate);
        yesterdayError.value = null;
      } else if (previousResult.reason?.code !== 'ERR_CANCELED') {
        yesterdayData.value = null;
        yesterdayError.value = previousResult.reason?.response?.data?.error
          || 'Failed to load yesterday dashboard data.';
      }
    }
  } catch (error) {
    if (requestId === requestSequence && error?.code !== 'ERR_CANCELED') {
      dashboardError.value = error.response?.data?.error || 'Failed to load dashboard data.';
    }
  } finally {
    if (requestId === requestSequence) isLoading.value = false;
  }
};

const handleUpdateSuccess = async () => {
  await refreshDashboard({ includeYesterday: isToday.value });
  startAutoRefresh();
};

const changeDay = days => {
  if (days > 0 && isToday.value) return;
  router.push(buildDashboardRoute(addDashboardDays(selectedDate.value, days)));
};

watch(
  () => route.params.date,
  async () => {
    if (!parsedDashboardRoute.value.valid) {
      await router.replace(buildDashboardRoute());
      return;
    }

    filterText.value = '';
    selectedDayData.value = null;
    yesterdayData.value = null;
    yesterdayError.value = null;
    hasLoaded.value = false;
    dashboardError.value = null;
    clearTimers();
    await refreshDashboard({ includeYesterday: isToday.value });
    startAutoRefresh();
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  requestController?.abort();
  clearTimers();
});
</script>

<style>
.dashboard-sections .nodata {
  color: #555555;
  font-size: 14px;
}

.dashboard-day + .dashboard-day {
  border-top: 1px solid var(--surface-border);
  padding-top: 1rem;
}
</style>
